/**
 * Les adresses de lecture d'une liste de fichiers (logos, vignettes) : une adresse à jeton chiffré par fichier, demandées
 * ensemble. Un échec isolé ne vide pas les autres (`allSettled`) : le fichier manquant est simplement absent de la table.
 *
 * `pret` passe à vrai quand TOUTES les réponses sont arrivées, réussies ou non : la feuille imprimée attend ce moment avant
 * de proposer d'imprimer, sinon elle partirait sans ses logos.
 */
import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';

export interface UrlsFichiers {
  readonly urls: Readonly<Record<string, string>>;
  readonly pret: boolean;
}

export function useUrlsFichiers(ids: readonly string[]): UrlsFichiers {
  // La clé change quand la liste change (pas à chaque rendu : `ids` est souvent reconstruit).
  const cle = ids.join('|');
  const liste = useMemo(() => (cle === '' ? [] : cle.split('|')), [cle]);
  const [etat, setEtat] = useState<UrlsFichiers>({ urls: {}, pret: liste.length === 0 });

  useEffect(() => {
    if (liste.length === 0) {
      setEtat({ urls: {}, pret: true });
      return;
    }
    let actif = true;
    setEtat((e) => ({ urls: e.urls, pret: false }));
    void Promise.allSettled(liste.map((id) => api.fileUrl(id))).then((resultats) => {
      if (!actif) return;
      const table: Record<string, string> = {};
      resultats.forEach((r, i) => {
        const id = liste[i];
        if (r.status === 'fulfilled' && id) table[id] = r.value.url;
      });
      setEtat({ urls: table, pret: true });
    });
    return () => {
      actif = false;
    };
  }, [liste]);

  return etat;
}
